/**
 * Pure predicates that gate the CRF Builder's two bulk-action buttons.
 *
 * Extracted out of CRFBuilder.tsx so the enable/disable logic is unit-testable
 * without React (web has no test runner; these are exercised via the functions
 * package's vitest). The shape below is a structural subset of CRFBuilder's
 * CRFForm, so the full form objects pass through unchanged.
 */
import { hasUsableRendering } from './crfFormDownloads';

export interface BulkActionForm {
  status: string;
  variables: unknown[] | null;
  crfGenerated?: boolean;
  sourceFilePath?: string;
  sourcePacketPath?: string;
}

/** Forms the bulk CRF render touches: it only ever renders approved forms. */
function isApproved(f: BulkActionForm): boolean {
  return f.status === 'approved' || f.status === 'final';
}

/**
 * A form "has a server rendering" when it carries a flag/path proving an
 * embeddable PDF exists: a fresh in-session render (`crfGenerated`), a
 * per-form clip or platform render (`sourceFilePath`), or a PDF packet it
 * was imported from. A Word-only `sourcePacketPath` does NOT count — the
 * Instruction Guide's rasterizer is PDF-only, so those forms still need the
 * bulk render (mirrors hasUsableRendering in crfFormDownloads).
 */
function isRendered(f: BulkActionForm): boolean {
  return Boolean(f.crfGenerated) || hasUsableRendering(f);
}

/**
 * A form with no variables needs variables, however that emptiness is spelled.
 *
 * Firestore stores `variables: null` on a freshly suggested form and the client
 * loads it as `[]`. Testing only for null therefore read an empty array as
 * "already done", which disabled "Generate All Variables" on a study where NO
 * form had any — while the header three inches away counted "0 with variables"
 * using a length test. Two rules for one question, disagreeing on screen.
 *
 * This is the length test, so the gate and the header can no longer contradict
 * each other. `crfBulkActions.test.ts` pins that they agree on every shape.
 */
export function formNeedsVariables(f: BulkActionForm): boolean {
  return !Array.isArray(f.variables) || f.variables.length === 0;
}

/**
 * Whether opening a form starts variable generation on its own.
 *
 * A failed generation is NOT retried automatically. Measured 2026-09-14 on
 * Clinical Dev: the client gave up at 70 s while the server ran 100-140 s,
 * the failure cleared generatingVars with the form still open and empty,
 * and the page fired again at once - 22 calls in 25 minutes, every result
 * discarded. Opening the form again clears the flag and retries once.
 */
export function shouldAutoGenerate(f: BulkActionForm & { expanded?: boolean; generatingVars?: boolean; generationFailed?: boolean }): boolean {
  return Boolean(f.expanded) && formNeedsVariables(f) && !f.generatingVars && !f.generationFailed;
}

/**
 * True when at least one approved form still lacks a server rendering — i.e.
 * the "Generate All CRFs" button has work to do. Goes false (button disabled)
 * once every approved form is rendered, and re-enables automatically when a new
 * form is later approved (a freshly approved form has no rendering yet).
 */
export function hasUnrenderedApprovedForm(forms: BulkActionForm[]): boolean {
  return forms.some((f) => isApproved(f) && !isRendered(f));
}

/**
 * True when there is any approved form the bulk render could act on —
 * including forms that already have a rendering.
 *
 * This is what gates the button, rather than `hasUnrenderedApprovedForm`.
 * Gating on "something is missing a rendering" made the action unavailable the
 * moment every form had one, so a rendering that was wrong or produced by an
 * older renderer could never be replaced in bulk. Re-rendering is safe to
 * repeat: it overwrites the platform's own render, and the server skips forms
 * whose canonical document is an upload.
 */
export function hasApprovedForm(forms: BulkActionForm[]): boolean {
  return forms.some(isApproved);
}

/**
 * True when at least one form still needs its variable list generated — i.e.
 * the "Generate All Variables" button has work to do. Goes false (button
 * disabled) once every form already has variables, and re-enables when a new
 * variable-less form is added.
 */
export function hasFormsNeedingVariables(forms: BulkActionForm[]): boolean {
  return forms.some(formNeedsVariables);
}
